import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { api, img, money } from "../../lib/api";
import { Btn, PageHeader } from "../../components/admin/ui";
import { BotFeedDialog } from "../../components/admin/BotFeedDialog";
import { Input } from "../../components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";

export const PRODUCT_STATUS = { published: ["منشور", "bg-green-50 text-green-700"], draft: ["مسودة", "bg-gray-100 text-gray-600"], unavailable: ["غير متوفر", "bg-amber-50 text-amber-700"] };

export const ConfirmDelete = ({ onConfirm, testid }) => (
  <AlertDialog>
    <AlertDialogTrigger data-testid={testid} className="rounded p-1.5 text-red-600 hover:bg-red-50"><Trash2 size={15} /></AlertDialogTrigger>
    <AlertDialogContent dir="rtl">
      <AlertDialogTitle>تأكيد الحذف؟</AlertDialogTitle>
      <AlertDialogFooter className="gap-2">
        <AlertDialogCancel>إلغاء</AlertDialogCancel>
        <AlertDialogAction data-testid={`${testid}-confirm`} onClick={onConfirm} className="bg-red-600 hover:bg-red-700">حذف</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export default function Products() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const load = () => api.get("/admin/products", { params: { q: q || undefined, status: status || undefined } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [q, status]); // eslint-disable-line

  const del = async (id) => {
    await api.delete(`/admin/products/${id}`);
    toast.success("تم حذف المنتج");
    load();
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/admin/products/export", { params: { origin: window.location.origin }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `qamar-products-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("تعذّر تصدير الملف");
    }
  };

  return (
    <div data-testid="admin-products">
      <PageHeader title="المنتجات والمخزون" desc={`${rows.length} منتج`}>
        <BotFeedDialog />
        <Btn variant="light" data-testid="export-products-csv-button" onClick={exportCsv}><Download size={16} />تصدير CSV</Btn>
        <Link to="/admin/products/new"><Btn data-testid="add-product-button"><Plus size={16} />إضافة منتج</Btn></Link>
      </PageHeader>
      <div className="mb-4 flex flex-wrap gap-2">
        <Input data-testid="products-search-input" placeholder="بحث بالاسم أو SKU" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs bg-white" />
        {[["", "الكل"], ...Object.entries(PRODUCT_STATUS).map(([k, v]) => [k, v[0]])].map(([k, l]) => (
          <button key={k} onClick={() => setStatus(k)} className={`rounded border px-3 text-sm ${status === k ? "border-ink bg-ink text-white" : "border-gray-300 bg-white"}`}>{l}</button>
        ))}
      </div>
      <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500">
            <tr className="text-right"><th className="p-3">المنتج</th><th>SKU</th><th>السعر</th><th>المخزون</th><th>الحالة</th><th>الوسوم</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} data-testid={`product-row-${p.sku}`} className="border-t border-gray-100">
                <td className="p-3"><div className="flex items-center gap-3"><img src={img(p.images?.[0])} alt="" className="h-12 w-9 object-cover" /><span className="font-medium">{p.name_ar}</span></div></td>
                <td className="font-mono text-xs">{p.sku}</td>
                <td>{p.sale_price ? <><span className="text-gold-deep">{money(p.sale_price)}</span> <s className="text-xs text-gray-400">{money(p.price)}</s></> : money(p.price)}</td>
                <td className={p.total_stock === 0 ? "font-bold text-red-600" : ""}>{p.total_stock}</td>
                <td><span className={`rounded px-2 py-0.5 text-xs ${PRODUCT_STATUS[p.status]?.[1]}`}>{PRODUCT_STATUS[p.status]?.[0]}</span></td>
                <td className="text-xs text-gray-500">{[p.is_new && "جديد", p.is_bestseller && "الأكثر مبيعًا", p.is_featured && "مميز", p.is_offer && "عرض"].filter(Boolean).join("، ")}</td>
                <td className="p-3"><div className="flex justify-end gap-1">
                  <Link to={`/admin/products/${p.id}`} data-testid={`edit-product-${p.sku}`} className="rounded p-1.5 hover:bg-gray-100"><Pencil size={15} /></Link>
                  <ConfirmDelete testid={`delete-product-${p.sku}`} onConfirm={() => del(p.id)} />
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
